import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import { joinClass, fetchMyClasses } from '../data/api';
import { useAuth } from '../../Accounts/AuthContext';

// Join-a-class, as a centered modal (Sandy 2026-08-12): opened from the side
// rail's "Join a class" item. Code field + the classes you're already in.
// Joining reloads on dismiss so every surface picks up the new enrollment.

function JoinSuccess({ cls }) {
  const done = () => window.location.reload();
  return (
    <div className="text-center">
      <div className="mx-auto w-12 h-12 rounded-full bg-success-50 text-success-600 flex items-center justify-center">
        <Check size={26} strokeWidth={3} />
      </div>
      <div className="mt-3 text-[18px] font-extrabold text-ink">You're in!</div>
      <p className="mt-1 mb-5 text-[13.5px] text-neutral-600 leading-relaxed">
        You joined {cls.period ? cls.period + ' · ' : ''}{cls.name}. Assignments from your teacher will show up in Up next.
      </p>
      <button
        type="button"
        onClick={done}
        className="h-10 px-6 rounded-full border-0 bg-ink hover:bg-neutral-800 text-white text-[14px] font-semibold cursor-pointer"
      >
        Let's go
      </button>
    </div>
  );
}

export default function JoinClassModal({ onClose }) {
  const { user, handleAPIResponse } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(null);
  const [myClasses, setMyClasses] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!user) return undefined;
    fetchMyClasses({ user, handleAPIResponse })
      .then((list) => { if (alive) setMyClasses(list); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  // Escape closes (joined state reloads instead, same as the backdrop).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (joined) window.location.reload();
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [joined, onClose]);
  const submit = async (e) => {
    e.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await joinClass({ user, handleAPIResponse }, code.trim());
      setJoined(r);
    } catch (err) {
      // Admin view mode refuses the join with a 409 of its own: say that,
      // never "already in that class".
      if (err.viewOnly) { setError(err.message); return; }
      const already = err.message === 'already joined' || err.status === 409;
      setError(err.message === 'invalid code' || err.status === 404
        ? "That code didn't match a class. Double-check it with your teacher."
        : already
          ? `You're already in ${err.data?.name ? (err.data.period ? err.data.period + ' · ' : '') + err.data.name : 'that class'}.`
          : err.message === 'wrong school' || err.status === 403
            ? 'That class is at a different school than your account.'
            : 'Could not join right now. Try again.');
    } finally {
      setBusy(false);
    }
  };
  // Portal to <body>: the rail that opens this is position:sticky, whose
  // stacking context would otherwise paint the modal UNDER the page's
  // z-indexed path cards.
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={joined ? () => window.location.reload() : onClose} />
      <div className="relative w-full max-w-[400px] bg-white rounded-[20px] shadow-[0_24px_64px_rgba(0,0,0,.18)] px-7 py-8">
        {!joined && (
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 rounded-full border-0 bg-neutral-100 hover:bg-neutral-200 text-neutral-500 hover:text-ink cursor-pointer flex items-center justify-center p-0"
          >
            <X size={15} strokeWidth={2.5} />
          </button>
        )}
        {joined ? (
          <JoinSuccess cls={joined} />
        ) : (
          <>
            <div className="text-[19px] font-extrabold text-ink">Join a class</div>
            <p className="m-0 mt-1.5 text-[13.5px] text-neutral-600 leading-relaxed">
              Type the code your teacher gave you. Your assignments show up automatically.
            </p>
            <form onSubmit={submit} className="mt-5 flex items-center gap-2">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXXXX"
                maxLength={9}
                autoFocus
                className="min-w-0 flex-1 h-11 text-[16px] font-extrabold tracking-[0.2em] text-center border border-neutral-300 rounded-[12px] outline-none focus:border-ink"
              />
              <button
                type="submit"
                disabled={busy || !code.trim()}
                className="flex-none h-11 px-6 rounded-full border-0 bg-ink hover:bg-neutral-800 text-white text-[14px] font-semibold cursor-pointer disabled:bg-neutral-300"
              >
                {busy ? 'Joining…' : 'Join'}
              </button>
            </form>
            {error && <p className="m-0 mt-2.5 text-[12.5px] font-semibold text-error-600">{error}</p>}
            {myClasses && myClasses.length > 0 && (
              <div className="mt-5 pt-4 border-t border-neutral-100">
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-400">Your classes</div>
                {myClasses.map((c) => (
                  <div key={c.classID} className="mt-1.5">
                    <div className="text-[13px] font-bold text-ink leading-snug">
                      {c.period ? c.period + ' · ' : ''}{c.name}
                    </div>
                    {c.teacherName && <div className="text-[12px] text-neutral-500 leading-snug">{c.teacherName}</div>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
